import { TFunction } from 'hooks/useI18n';
import { ListItem } from 'interfaces/ui/ui-list-item';
import isEmpty from 'lodash/isEmpty';
import { Location } from 'model';
import { OffersListFilter } from 'model/model-offers-list-filter';
import { getCityStringFromUIPlacesAutocompleteItem } from 'services/ui/offer.service';
import { formatDateTime } from './date';
import { GetJobOfferAlertName } from './job-offers-alerts.types';

type JobOfferAlertNameCriteria = {
  isFulfilled: boolean;
  text: string | undefined;
};

type BuildJobOfferAlertNameCriteriaArgs = {
  filter: OffersListFilter;
  techs: ListItem[];
  techRoles: ListItem[];
  t: TFunction;
  locale?: string;
};

const buildJobOfferAlertNameCriteria = ({
  filter,
  techs,
  techRoles,
  t,
  locale
}: BuildJobOfferAlertNameCriteriaArgs): JobOfferAlertNameCriteria[] => {
  return [
    {
      isFulfilled: !isEmpty(filter.techRoles),
      text:
        filter.techRoles
          ?.map((techRoleId) =>
            techRoles.find((tech) => tech.value === techRoleId)
          )
          .map((techRole) => techRole?.label)
          .filter(Boolean)
          .join(', ') || ''
    },

    {
      isFulfilled: isEmpty(filter.techRoles) && !isEmpty(filter.text),
      text: filter.text
    },

    {
      isFulfilled: !isEmpty(filter.locations) || !isEmpty(filter.remoteOptions),
      text: `${
        !isEmpty(filter.locations)
          ? t('offerAlertModal.item.at', {
              value: getCityNames(filter.locations, locale)
            })
          : ''
      }${
        !isEmpty(filter.locations) && !isEmpty(filter.remoteOptions)
          ? ' ' +
            t('offerAlertModal.item.plus', {
              value:
                filter.remoteOptions
                  ?.map((opt) =>
                    t(`remote_options_items.${opt}`, { ns: 'job-offer-list' })
                  )
                  .join(', ') || ''
            })
          : filter.remoteOptions
              ?.map((opt) =>
                t(`remote_options_items.${opt}`, { ns: 'job-offer-list' })
              )
              .join(', ') || ''
      }`
    },

    {
      isFulfilled: !isEmpty(filter.techs),
      text: t('offerAlertModal.item.with', {
        value:
          filter.techs
            ?.map((techId) => techs.find((tech) => tech.value === techId))
            .map((tech) => tech?.label)
            .filter(Boolean)
            .join(', ') || ''
      })
    },

    {
      isFulfilled: !!filter.minSalary,
      text: t('offerAlertModal.item.for', {
        value: filter.minSalary,
        currency: filter.currency
      })
    },

    {
      isFulfilled: !isEmpty(filter.minLanguages),
      text: t('offerAlertModal.item.minLanguages', {
        value: filter.minLanguages
          ?.map(
            (minLang) =>
              `${minLang.language.label} (${t(
                'language-selector.level.options.' + minLang.level.value,
                { ns: 'common' }
              )})`
          )
          .join(', ')
      })
    },

    {
      isFulfilled: !!filter.offerLanguage,
      text: t('offerAlertModal.item.in', {
        value: t(`offerAlertModal.item.lang.${filter.offerLanguage}`)
      })
    }
  ];
};

export const getJobOfferAlertName: GetJobOfferAlertName = ({
  alert,
  techs,
  techRoles,
  t,
  locale,
  criteriaCount = 3
}) => {
  const criteria = buildJobOfferAlertNameCriteria({
    filter: alert.filter,
    techs,
    techRoles,
    t,
    locale
  });

  if (criteria.length <= 0) {
    return `${t('offerAlertModal.alert', {
      date: formatDateTime(alert.createdAt, locale)
    })}`;
  }
  return criteria
    .filter((c) => c.isFulfilled && !!c.text)
    .map((c) => c.text)
    .slice(0, criteriaCount)
    .join(' ')
    .trim();
};

function getCityNames(cities?: Location[], locale?: string): string {
  if (!cities) {
    return '';
  }

  const parsedCities = getCityStringFromUIPlacesAutocompleteItem(
    cities,
    locale
  ).split(', ');

  return getArrayValue(parsedCities);
}

function getArrayValue(val: string[] = [], MAX_ELEMENTS = 2): string {
  const suffix =
    val.length > MAX_ELEMENTS ? ` (+${val.length - MAX_ELEMENTS})` : '';

  return `${val?.slice(0, MAX_ELEMENTS).join(', ')}${suffix}`;
}
